import React, { useEffect, useRef, useState } from "react";
import { Check, ImagePlus, X, Download, Smartphone, HardDrive } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { PRESET_COLORS, PRESET_GRADIENTS, PRESET_IMAGES } from "@/i18n";
import { storageEstimate } from "@/lib/db";
import { formatBytes } from "@/lib/format";

function Section({ title, children }) {
  return (
    <section className="rounded-2xl bg-white/[0.04] border border-white/10 p-4">
      <h3 className="font-display font-semibold text-sm uppercase tracking-wider text-white/50 mb-3">{title}</h3>
      {children}
    </section>
  );
}

export function SettingsView() {
  const { bg, setBg, language, setLanguage, t, canInstall, installed, promptInstall } = useApp();
  const fileRef = useRef(null);
  const [stats, setStats] = useState({ usage: 0, quota: 0 });

  useEffect(() => {
    storageEstimate().then(setStats);
  }, []);

  const onImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setBg({ ...bg, type: "image", image: reader.result });
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const pct = stats.quota ? Math.min(100, (stats.usage / stats.quota) * 100) : 0;

  return (
    <div className="pt-4 space-y-4 fade-up pb-4">
      <Section title={t("install")}>
        {installed ? (
          <p data-testid="install-installed" className="text-sm text-emerald-400 font-medium">{t("installed")}</p>
        ) : (
          <>
            <p className="text-sm text-white/60 mb-3">{t("installHint")}</p>
            {canInstall ? (
              <button data-testid="install-btn" onClick={promptInstall} className="w-full flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#ff3366] to-[#a020f0] py-3 font-semibold active:scale-[0.98] transition-transform">
                <Download className="h-5 w-5" /> {t("installAction")}
              </button>
            ) : (
              <div data-testid="install-hint" className="flex items-start gap-2 rounded-xl bg-white/5 p-3 text-sm text-white/60">
                <Smartphone className="h-5 w-5 shrink-0 mt-0.5 text-[#ff3366]" />
                <span>{isIos ? t("installIosHint") : t("installHint")}</span>
              </div>
            )}
          </>
        )}
      </Section>

      <Section title={t("personalization")}>
        <p className="text-xs text-white/40 mb-2">{t("color")}</p>
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 mb-4">
          {PRESET_COLORS.map((c) => {
            const active = bg.type === "color" && bg.color === c;
            return (
              <button
                key={c}
                data-testid={`bg-color-${c}`}
                onClick={() => setBg({ ...bg, type: "color", color: c })}
                className={`relative h-12 w-12 shrink-0 rounded-full border-2 transition-transform ${active ? "border-white scale-105" : "border-white/15"}`}
                style={{ backgroundColor: c }}
              >
                {active && <Check className="absolute inset-0 m-auto h-5 w-5 text-white" />}
              </button>
            );
          })}
        </div>

        <p className="text-xs text-white/40 mb-2">{t("gradient")}</p>
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 mb-4">
          {PRESET_GRADIENTS.map((g, i) => {
            const active = bg.type === "gradient" && bg.gradient === g;
            return (
              <button
                key={i}
                data-testid={`bg-gradient-${i}`}
                onClick={() => setBg({ ...bg, type: "gradient", gradient: g })}
                className={`relative h-12 w-12 shrink-0 rounded-full border-2 transition-transform ${active ? "border-white scale-105" : "border-white/15"}`}
                style={{ backgroundImage: g }}
              >
                {active && <Check className="absolute inset-0 m-auto h-5 w-5 text-white" />}
              </button>
            );
          })}
        </div>

        <p className="text-xs text-white/40 mb-2">{t("image")}</p>
        <div className="grid grid-cols-3 gap-2">
          {PRESET_IMAGES.map((img, i) => {
            const active = bg.type === "image" && bg.image === img;
            return (
              <button
                key={i}
                data-testid={`bg-image-${i}`}
                onClick={() => setBg({ ...bg, type: "image", image: img })}
                className={`relative aspect-square rounded-xl overflow-hidden border-2 transition-transform ${active ? "border-white scale-[1.02]" : "border-white/10"}`}
              >
                <img src={img} alt="" className="h-full w-full object-cover" />
                {active && <span className="absolute inset-0 grid place-items-center bg-black/40"><Check className="h-6 w-6 text-white" /></span>}
              </button>
            );
          })}
          <button
            data-testid="bg-custom-image-btn"
            onClick={() => fileRef.current?.click()}
            className="aspect-square rounded-xl border-2 border-dashed border-white/20 grid place-items-center text-white/50 hover:border-white/40 transition-colors"
          >
            <ImagePlus className="h-6 w-6" />
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onImage} />
        </div>
        {bg.type === "image" && (
          <button data-testid="clear-bg-btn" onClick={() => setBg({ ...bg, type: "gradient" })} className="mt-3 flex items-center gap-1.5 text-sm text-white/50 hover:text-white transition-colors">
            <X className="h-4 w-4" /> {t("clearImage")}
          </button>
        )}
      </Section>

      <Section title={t("language")}>
        <div className="grid grid-cols-2 gap-2">
          {[
            { k: "pt", label: "Português" },
            { k: "en", label: "English" },
          ].map((l) => (
            <button
              key={l.k}
              data-testid={`lang-${l.k}`}
              onClick={() => setLanguage(l.k)}
              className={`rounded-xl py-2.5 text-sm font-medium transition-colors ${language === l.k ? "bg-[#ff3366] text-white" : "bg-white/5 text-white/60 hover:bg-white/10"}`}
            >
              {l.label}
            </button>
          ))}
        </div>
      </Section>

      <Section title={t("storage")}>
        <div className="flex items-center gap-3 mb-2">
          <HardDrive className="h-5 w-5 text-[#ff3366]" />
          <p data-testid="storage-stats" className="text-sm text-white/80">
            {formatBytes(stats.usage)} {t("used")}
            {stats.quota ? ` ${t("of")} ${formatBytes(stats.quota)}` : ""}
          </p>
        </div>
        <div className="h-2 w-full rounded-full bg-white/10 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#ff3366] to-[#a020f0]" style={{ width: `${pct}%` }} />
        </div>
        <p className="text-xs text-white/40 mt-2">{t("aboutStorage")}</p>
      </Section>
    </div>
  );
}
